import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdTextarea } from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import * as EmployeeService from './EmployeeService';
import ConfirmDialog from '../ConfirmDialog';
import { IEmployeeConfrimDialog } from './interfaces';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import LoaderService from '../../Services/LoaderService';

interface IFormValues {
  reason: string;
}

function BlockUser(props: IEmployeeConfrimDialog): JSX.Element {
  const { formatMessage: f } = useIntl();

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<IFormValues>({
    mode: 'onChange',
    defaultValues: {
      reason: '',
    },
    resolver: (data) => {
      const maxLength = 500;
      const validateErros = validateFields(data, {
        reason: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
          {
            param: 'maxLength',
            value: 500,
            message: f({ id: 'form.maxLength' }, { value: maxLength }),
          },
        ],
      });
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const closeConfirmDialog = () => {
    props.handleConfirmDialogOpen(false);
  };

  const handleSubmitDeactiveConfirmDialog = async (values: IFormValues) => {
    LoaderService.showLoader();
    const res = await EmployeeService.deactivate(props.selectedIds[0], values.reason);

    if (res) {
      props.updateTable();
      props.handleConfirmDialogOpen(false);
    }
    LoaderService.showLoader(false);
  };

  const handleSubmitMultipleDeactiveConfirmDialog = async (values: IFormValues) => {
    LoaderService.showLoader();
    const res = await EmployeeService.deactivateMultiple(props.selectedIds, values.reason);
    if (res) {
      props.updateTable();
      props.handleConfirmDialogOpen(false);
    }
    LoaderService.showLoader(false);
  };

  const renderTextarea = () => {
    return (
      <Controller
        name="reason"
        control={control}
        render={({ field }) => (
          <CmdTextarea
            {...field}
            id="reason"
            label={f({ id: 'employees.confirm.dialog.reason' })}
            setValue={(value) => {
              setValue('reason', value);
              trigger('reason');
            }}
            error={errors?.reason?.message}
            touched={!!errors?.reason?.message}
            autoFocus
            required
          />
        )}
      />
    );
  };

  return props.selectedIds.length > 1 ? (
    <ConfirmDialog
      title={`${f({ id: 'employee.confirm.dialog.multiple.block.title' })}`}
      cancelCallback={closeConfirmDialog}
      continueCallback={handleSubmit(handleSubmitMultipleDeactiveConfirmDialog)}
      cancelButtonTitle={f({
        id: 'form.cancel',
      })}
      continueButtonTitle={f({
        id: 'table_list.actions.block',
      })}
    >
      <div style={{ margin: '0 8px 24px 8px' }}>
        {f(
          { id: 'table.confirm.dialog.multiple.block.message' },
          { count: props.selectedIds.length }
        )}
      </div>

      {renderTextarea()}
    </ConfirmDialog>
  ) : (
    <ConfirmDialog
      title={`${f({ id: 'employee.confirm.dialog.block.title' })}`}
      cancelCallback={closeConfirmDialog}
      continueCallback={handleSubmit(handleSubmitDeactiveConfirmDialog)}
      cancelButtonTitle={f({
        id: 'form.cancel',
      })}
      continueButtonTitle={f({
        id: 'table_list.actions.block',
      })}
    >
      <div style={{ margin: '0 8px 24px 8px' }}>
        {f({ id: 'table.confirm.dialog.block.message' }, { name: `${props.fullname}` })}
      </div>
      {renderTextarea()}
    </ConfirmDialog>
  );
}

export default BlockUser;
